<template>
  <MainFormLayout
    bg-class="bg-faint-grey"
    :isMainHeading="true">
    <template #pageGuidance>
      <p class="mb-8 md:mb-12">
        Please select the type of article you would like to submit.
      </p>

      <h2 class="font-bitter text-2xl font-bold pb-4">
        Required information for submission
      </h2>
      <ul class="list-disc pl-10">
        <li>
          Manuscript document
        </li>
        <li>
          Cover letter
        </li>
        <li>
          <a
            href="https://www.rsc.org/journals-books-databases/author-and-reviewer-hub/authors-information/prepare-and-format/data-sharing/#dataavailabilitystatements"
            id="lnk-requiredInfo-das"
            target="_blank"
            class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
          >Data Availability Statement</a>
          (DAS)
        </li>

        <li>
          Author names, emails and affiliations
        </li>
        <li>
          A minimum of two recommended reviewer names and their email addresses
        </li>
      </ul>
      <p class="mt-4 md:mb-8">
        Read our full guidance on
        <a
          href="https://www.rsc.org/publishing/publish-with-us/publish-a-journal-article/chemical-science"
          target="_blank"
          class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
          id="lnk-requiredInfo-prepareArticle"
        >how to prepare your article for submission</a>.
      </p>
    </template>

    <div class="md:py-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4">
      <!-- Card Edge Article -->
      <CardSingle>
        <template #cardTitle>
          Edge Article
        </template>
        <template #cardDescription>
          Original research of exceptional significance and novelty, appealing to a wide general
          interest readership. Edge articles have no page limit.
        </template>
        <template #cardButton>
          <ButtonComponent
            label="Submit now"
            variant="primary"
            data-testid="submit-edge-article"
            id="edgeArticleSubmitBtn"
            @customClick="handleArticleSelection('EdgeArticle', 'edgeArticleSubmitBtn')"
          />
        </template>
      </CardSingle>

      <!-- Card Review Article -->
      <CardSingle>
        <template #cardTitle>
          Review Article
        </template>
        <template #cardDescription>
          A concise and critical appraisal of a research area that will be of interest to the
          general readership of the journal, normally submitted upon invitation.
        </template>
        <template #cardButton>
          <ButtonComponent
            label="Submit now"
            variant="secondary"
            id="reviewArticleSubmitBtn"
            @customClick="handleArticleSelection('ReviewArticle', 'reviewArticleSubmitBtn')"
          />
        </template>
      </CardSingle>

      <!-- Card Perspective -->
      <CardSingle>
        <template #cardTitle>
          Perspective
        </template>
        <template #cardDescription>
          A personalised account of a research area providing a personal or speculative viewpoint
          on a given subject and its future development, normally submitted upon invitation.
        </template>
        <template #cardButton>
          <ButtonComponent
            label="Submit now"
            variant="secondary"
            id="perspectiveSubmitBtn"
            @customClick="handleArticleSelection('Perspective', 'perspectiveSubmitBtn')"
          />
        </template>
      </CardSingle>

      <!-- Card Comment -->
      <CardSingle>
        <template #cardTitle>
          Comment
        </template>
        <template #cardDescription>
          A medium for the discussion and exchange of scientific opinions concerning material
          published in Chemical Science.
        </template>
        <template #cardButton>
          <ButtonComponent
            label="Submit now"
            variant="secondary"
            id="commentSubmitBtn"
            @customClick="handleArticleSelection('Comment', 'commentSubmitBtn')"
          />
        </template>
      </CardSingle>
    </div>

    <NotificationComponent
      v-if="hasStartSubmissionError"
      data-testid="failed-to-start-submission"
      class="mb-8"
      role="alert"
      variant="error"
      heading-text="Failed to start submission"
      :heading-level="2">
      <template #body>
        <p>
          Unable to start a new submission. Please retry or <a
            data-testid="email-customer-service-start-submission"
            href="mailto:manuscriptsupport@rsc.org?subject=RSC%20Submissions%3A%20Failed%20to%20start%20submission"
            class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited">email
            customer services</a> if the issue persists.
        </p>
      </template>
    </NotificationComponent>
  </MainFormLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import MainFormLayout from '../layouts/MainFormLayout.vue'
import ButtonComponent from '../components/ui-components/ButtonComponent.vue'
import CardSingle from '../components/common/CardSingle.vue'
import NotificationComponent from '../components/ui-components/NotificationComponent.vue'
import { checkLocalStorageExpiration, checkLocalStorageData, checkLocalSubmissionValidity } from '../services/localStorageManager'
import { trackEvent } from '../services/gtm'
import { AxiosError } from 'axios'
import { ArticleType } from '@/types'
import { identifyRouteFromStep } from '../store/functions'
import { ARTICLE_TYPES } from '../constants/app'

const router = useRouter()
const store = useStore()
const hasStartSubmissionError = ref(false)

const trackStartSubmissionError = (error: AxiosError | unknown) => {
  const status = (error as AxiosError)?.response?.status
  const message = (error as AxiosError)?.message
  trackEvent({
    category: 'Server Errors',
    action: 'error',
    event: 'startSubmissionError',
    errorType: status ? `HTTP${status}` : 'UnknownError',
    errorDetails: message || 'No error message available',
    submissionId: store.state.activeSubmission.submissionId,
  })
}

function handleArticleSelection(articleType: string, buttonId: string) {
  if (!isValidArticleType(articleType)) {
    throw new Error(`Invalid article type: ${articleType}`)
  }

  trackEvent({
    event: 'ArticleTypeSelected',
    category: 'Article Type Selection',
    action: 'click',
    buttonId,
  })

  navigateToNextStep(articleType as ArticleType)
}

function isValidArticleType(articleType: string): articleType is ArticleType {
  return (ARTICLE_TYPES as readonly string[]).includes(articleType)
}

async function navigateToNextStep(articleType: ArticleType) {
  const localStorageData = JSON.parse(localStorage.getItem('rsc_submission')!)

  if (checkLocalStorageData(localStorageData)) {
    const isValidSubmission
      = !checkLocalStorageExpiration(store, localStorageData)
        && checkLocalSubmissionValidity(store, localStorageData, articleType)

    if (isValidSubmission) {
      router.push({ name: identifyRouteFromStep(localStorageData) })
    }
    else {
      await startNewSubmission(articleType)
    }
  }
  else {
    await startNewSubmission(articleType)
  }
}

async function startNewSubmission(articleType: ArticleType) {
  try {
    hasStartSubmissionError.value = false
    await store.dispatch('startNewSubmission', articleType)
    router.push({ name: 'upload-files' })
  }
  catch (error) {
    trackStartSubmissionError(error)
    hasStartSubmissionError.value = true
  }
}

</script>
